<template>
  <div v-if="isModalOpen" class="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50" @click="closeModal">
    <div class="bg-light-bg dark:bg-dark-bg w-3/4 h-3/4 rounded-lg shadow-lg flex" @click.stop>
      
      <div class="w-1/3 rounded-lg overflow-y-auto bg-sidebar-bg dark:bg-dark-sidebar-bg sidebar-content">
        <div class="p-4 sticky top-0 bg-sidebar-bg dark:bg-dark-sidebar-bg z-10">
          <input 
            v-model="searchQuery"
            type="text"
            class="block w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
            placeholder="Wyszukaj kierowcę..."
          />
        </div>

        <template v-if="filteredDrivers.length > 0">
            <div
              v-for="driver in filteredDrivers"
              :key="driver.id"
              @click="selectDriver(driver)"
              class="flex items-center p-4 cursor-pointer dark:hover:bg-teal-700"
              :class="{ 
                'bg-gray-300 hover:bg-gray-400 dark:bg-teal-600': activeChatDriverId === driver.id,
                'bg-teal-600 hover:bg-gray-500 dark:bg-teal-800': newMessagesFrom.includes(driver.id) && activeChatDriverId !== driver.id,
                'bg-gray-200 hover:bg-gray-300 dark:bg-teal-600': !newMessagesFrom.includes(driver.id)
              }"
            >
            <div class="ml-3">
              <p class="font-semibold dark:text-white">{{ driver.firstName }} {{ driver.lastName }}</p>
              <span class="text-sm"
              :class="{ 
                'text-gray-500 dark:text-gray-200': activeChatDriverId === driver.id,
                'text-gray-100 dark:text-gray-300': newMessagesFrom.includes(driver.id) && activeChatDriverId !== driver.id,
                'text-gray-500 dark:text-gray-200': !newMessagesFrom.includes(driver.id)
              }"
              
              >
                {{ driver.lastMessage ? driver.lastMessage : 'Brak wiadomości' }}
              </span>
            </div>
          </div>
        </template>
        <p v-else class="text-gray-500 text-center p-4">Brak kierowców</p>
      </div>

      <div class="flex-1 flex flex-col justify-between px-5 py-4 bg-sidebar-bg dark:bg-dark-sidebar-bg rounded-lg">
        <div v-if="activeChatDriverId" class="overflow-y-auto flex flex-col gap-4 bg-sidebar-bg dark:bg-dark-sidebar-bg sidebar-content" ref="chatWindow">
          <div v-for="message in messages" :key="message.id" :class="message.isUser ? 'flex justify-end' : 'flex justify-start'">
            <div :class="message.isUser ? 'bg-teal-600 text-white' : 'bg-gray-600 text-white'" class="p-3 rounded-lg max-w-xs mr-4">
              <div class="text-xs text-gray-400">{{ formatTimestamp(message.timestamp) }}</div>
              <div>{{ message.text }}</div>
            </div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-center p-4">Wybierz kierowcę, aby rozpocząć rozmowę</p>

        <div class="mt-4 flex">
          <input 
            v-model="newMessage"
            type="text"
            :class="{
              'border-red-500': isInputInvalid, 
              'dark:border-red-500': isInputInvalid
            }"
            class="block w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text" 
            placeholder="Wprowadź swój tekst..."
            @keyup.enter="sendMessage"
            @input="isInputInvalid = false"
          />
          <button @click="sendMessage" class="ml-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-lg">Wyślij</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import messagesService from '@/services/messagesService';

export default {
  name: "ChatModal",
  props: {
    isModalOpen: Boolean,
    drivers: Array,
    senderId: Number,
  },
  data() {
    return {
      newMessage: "",
      messages: [],
      activeChatDriverId: null,
      isInputInvalid: false,
      // Używamy localDrivers do wyświetlania, żeby móc modyfikować stan
      localDrivers: [],
      newMessagesFrom: [],
      refreshIntervalId: null,
      searchQuery: "",
    };
  },
  watch: {
    isModalOpen(newVal) {
      if (newVal) {
        // Gdy modal się otwiera, tworzymy kopię propa i ładujemy wiadomości
        this.localDrivers = JSON.parse(JSON.stringify(this.drivers));
        this.loadAllLastMessages();
      } else {
        // Gdy modal się zamyka, czyścimy stan
        this.messages = [];
        this.activeChatDriverId = null;
        this.localDrivers = [];
        this.searchQuery = "";
      }
    },
    // Obserwujemy zmiany w propie 'drivers', aby aktualizować listę
    drivers: {
      handler(newDrivers) {
        if (newDrivers.length > 0 && this.isModalOpen) {
          this.localDrivers = JSON.parse(JSON.stringify(newDrivers));
          this.loadAllLastMessages();
        }
      },
      deep: true,
    }
  },
  computed: {
    sortedDrivers() {
      const activeId = this.activeChatDriverId;
      return [...this.localDrivers].sort((a, b) => {
        // Pobierz timestampy, z wartością 0 dla kierowców bez wiadomości

        if (activeId) {
          if (a.id === activeId && b.id !== activeId) {
            return -1;
          }
          if (b.id === activeId && a.id !== activeId) {
            return 1;
          }
        }

        const timeA = a.lastMessageTimestamp ? a.lastMessageTimestamp.getTime() : 0;
        const timeB = b.lastMessageTimestamp ? b.lastMessageTimestamp.getTime() : 0;

        // 1. Sortowanie po dacie wiadomości (malejąco)
        if (timeB - timeA !== 0) {
          return timeB - timeA;
        }

        // 2. Jeśli timestampy są równe (np. obie 0), sortuj alfabetycznie po imieniu
        const nameA = a.firstName.toLowerCase();
        const nameB = b.firstName.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },
    filteredDrivers() {
      if (!this.searchQuery) {
        // Jeśli pole wyszukiwania jest puste, zwracamy posortowaną listę.
        return this.sortedDrivers;
      }

      const query = this.searchQuery.toLowerCase();
      // Filtrujemy listę na podstawie imienia lub nazwiska.
      return this.sortedDrivers.filter(driver => {
        // Upewniamy się, że driver.firstName i driver.lastName istnieją, zanim je użyjemy.
        const firstName = driver.firstName ? driver.firstName.toLowerCase() : '';
        const lastName = driver.lastName ? driver.lastName.toLowerCase() : '';
        
        return firstName.includes(query) || lastName.includes(query);
      });
    }
  },
  methods: {
    scrollToBottom() {
      const chatWindow = this.$refs.chatWindow;
      if (chatWindow) {
        chatWindow.scrollTop = chatWindow.scrollHeight;
      }
    },
    closeModal() {
      this.$emit("close");
      this.activeChatDriverId = null;
      // this.messages = [];
    },
    selectDriver(driver) {
      // Zatrzymujemy poprzednie odświeżanie (jeśli istnieje)
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId);
        this.refreshIntervalId = null;
      }

      this.activeChatDriverId = driver.id;
      this.messages = [];
      this.loadChatHistory(driver.id);
      this.newMessagesFrom = this.newMessagesFrom.filter(id => id !== driver.id);

      this.searchQuery = "";

      this.refreshIntervalId = setInterval(() => {
        if (this.activeChatDriverId) {
          this.loadChatHistory(this.activeChatDriverId);
        }
      }, 5000);
    },
    beforeUnmount() {
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId);
      }
    },
    async sendMessage() {
      if (this.newMessage.trim() === "") {
        this.isInputInvalid = true;
        return;
      }

      try {
        const message = await messagesService.sendMessage(this.senderId, this.activeChatDriverId, this.newMessage);
        this.messages.push({
          id: message.id,
          text: message.content,
          timestamp: message.timestamp,
          isUser: true,
        });
        this.newMessage = "";
        this.isInputInvalid = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        this.loadLastMessage(this.activeChatDriverId);
      } catch (error) {
        console.error("Błąd podczas wysyłania wiadomości:", error);
      }
    },
    async loadChatHistory(driverId) {
        try {
            const history = await messagesService.getChatHistory(this.senderId, driverId);
            this.messages = history.map(msg => {
                let isUser = false;
                if (msg.sender && msg.sender.id === this.senderId) {
                    isUser = true;
                }

                return {
                    id: msg.id,
                    text: msg.content,
                    timestamp: msg.timestamp,
                    isUser: isUser,
                };
            });
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        } catch (error) {
            console.error("Błąd podczas ładowania historii czatu:", error);
        }
    },
    async loadLastMessage(driverId) {
      try {
        const lastMessage = await messagesService.getLastMessage(this.senderId, driverId);
        const driverToUpdate = this.localDrivers.find(d => d.id === driverId);

        if (driverToUpdate && lastMessage) {
          let messagePrefix = '';

          const isNewMessageFromDriver = lastMessage.sender && lastMessage.sender.id !== this.senderId;

          if (isNewMessageFromDriver) {
            messagePrefix = 'Kierowca: ';
            // Dodajemy ID kierowcy do tablicy
            if (!this.newMessagesFrom.includes(driverId)) {
              this.newMessagesFrom.push(driverId);
            }
          } else {
            messagePrefix = 'Ty: ';
          }

          driverToUpdate.lastMessage = messagePrefix + lastMessage.content;
          // Dodajemy znacznik czasu ostatniej wiadomości
          driverToUpdate.lastMessageTimestamp = new Date(lastMessage.timestamp); 
        } else if (driverToUpdate) {
          driverToUpdate.lastMessage = "Brak wiadomości";
          // Jeśli brak wiadomości, ustawiamy bardzo starą datę, aby byli na końcu listy
          driverToUpdate.lastMessageTimestamp = new Date(0); 
        }
      } catch (error) {
        console.error("Błąd podczas ładowania ostatniej wiadomości:", error);
        const driverToUpdate = this.localDrivers.find(d => d.id === driverId);
        if (driverToUpdate) {
          driverToUpdate.lastMessage = "Brak wiadomości";
          driverToUpdate.lastMessageTimestamp = new Date(0);
        }
      }
    },
    async loadAllLastMessages() {
      if (this.localDrivers.length > 0) {
        const promises = this.localDrivers.map(driver => this.loadLastMessage(driver.id));
        await Promise.all(promises);
        // Po załadowaniu wszystkich wiadomości, sortujemy listę kierowców
        this.sortDriversByLatestMessage();
      }
    },
    sortDriversByLatestMessage() {
      this.localDrivers.sort((a, b) => {
        // Sortujemy malejąco, od najnowszej do najstarszej
        const timeA = a.lastMessageTimestamp ? a.lastMessageTimestamp.getTime() : 0;
        const timeB = b.lastMessageTimestamp ? b.lastMessageTimestamp.getTime() : 0;
        return timeB - timeA;
      });
    },
    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString("pl-PL", {
        weekday: "short", 
        year: "numeric", 
        month: "short", 
        day: "numeric", 
        hour: "2-digit", 
        minute: "2-digit", 
      });
    },
  },
};
</script>

<style scoped>
/* Ciemna nakładka */
.transition-opacity {
  transition: opacity 0.3s ease-in-out;
}

button[v-show="false"] {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s ease, visibility 0.1s ease;
}

/* Blokada przewijania całej strony */
body.overflow-hidden {
  overflow: hidden;
}

/* Styl dla zawartości sidebaru */
.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto; /* Przewijanie, jeśli zawartość się nie mieści */
}

.sidebar-content ul li a,
.sidebar-content button,
.sidebar-content a {
  font-size: 1rem; /* Domyślny rozmiar tekstu */
  padding: 0.75rem 1rem; /* Domyślny padding */
  transition: font-size 0.3s ease, padding 0.3s ease; /* Płynne przejścia */
}

/* Styl dla stopki sidebaru */
.sidebar-footer {
  position: sticky;
  bottom: 0;
  background-color: inherit; /* Zachowanie tła sidebaru */
  padding: 1rem; /* Podstawowy padding */
  z-index: 10; /* Priorytet na warstwy */
  display: flex;
  flex-direction: column;
  gap: 0.75rem; /* Dodanie odstępów między elementami */
}

/* Styl dla sticky headera w sidebarze */
.sidebar-header {
  z-index: 10; /* Aby była nad elementami nawigacji */
}

/* Zmniejszenie przycisków i tekstów dla większych skal */
@media (min-width: 768px) and (max-width: 1024px) {
  .sidebar-content ul li a,
  .sidebar-content button,
  .sidebar-content a {
    font-size: 0.9rem; /* Zmniejszony tekst */
    padding: 0.6rem 0.8rem; /* Zmniejszony padding */
  }
}

@media (min-width: 1025px) and (max-height: 900px) {
  .sidebar-content ul li a,
  .sidebar-content button,
  .sidebar-content a {
    font-size: 0.8rem; /* Jeszcze mniejszy tekst */
    padding: 0.5rem 0.7rem; /* Jeszcze mniejszy padding */
  }
}

@media (max-height: 600px) {
  .sidebar-content ul li a,
  .sidebar-content button,
  .sidebar-content a {
    font-size: 0.75rem; /* Minimalny tekst */
    padding: 0.4rem 0.6rem; /* Minimalny padding */
  }
}

/* Styl dla ikon w sidebarze */
.sidebar-content ul li span svg,
.sidebar-content button span svg,
.sidebar-content a span svg {
  width: 1.5rem; /* Domyślny rozmiar ikon */
  height: 1.5rem;
  transition: width 0.3s ease, height 0.3s ease;
}

/* Zmniejszenie ikon dla większych skal */
@media (min-width: 768px) and (max-width: 1024px) {
  .sidebar-content ul li span svg,
  .sidebar-content button span svg,
  .sidebar-content a span svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}

@media (min-width: 1025px) and (max-height: 900px) {
  .sidebar-content ul li span svg,
  .sidebar-content button span svg,
  .sidebar-content a span svg {
    width: 1rem;
    height: 1rem;
  }
}

@media (max-height: 600px) {
  .sidebar-content ul li span svg,
  .sidebar-content button span svg,
  .sidebar-content a span svg {
    width: 0.8rem;
    height: 0.8rem;
  }
}



/* Pasek przewijania w trybie ciemnym */
.dark .sidebar-content::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.dark .sidebar-content::-webkit-scrollbar-thumb {
  background-color: #2c7a7a; /* Teal-700 */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #1a5d5d; /* Ciemniejszy odcień teal dla krawędzi suwaka */
}

.dark .sidebar-content::-webkit-scrollbar-track {
  background-color: #333333; /* Ciemne tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}

/* Pasek przewijania w trybie jasnym */
.sidebar-content::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.sidebar-content::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* Jasnoszary kolor suwaka (szary) */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #9ca3af; /* Ciemniejszy szary dla krawędzi suwaka */
}

.sidebar-content::-webkit-scrollbar-track {
  background-color: #f3f4f6; /* Jasnoszare tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}

/* Styl dla sekcji nawigacyjnej z paskiem przewijania po lewej stronie */
.sidebar-navigation {
  direction: rtl; /* Przenosi scrollbar na lewą stronę */
  text-align: left; /* Przywraca wyrównanie tekstu */
  max-height: calc(100vh - 10rem); /* Dopasowanie wysokości */
  overflow-y: auto; /* Włączenie przewijania */
  padding-right: 1rem; /* Odsunięcie treści od scrollbara */
}

/* Resetowanie kierunku dla zawartości wewnętrznej sekcji nawigacyjnej */
.sidebar-navigation * {
  direction: ltr; /* Przywrócenie standardowego kierunku tekstu */
}

/* Pasek przewijania w trybie jasnym */
.sidebar-navigation::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.sidebar-navigation::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* Jasnoszary kolor suwaka */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #f3f4f6; /* Lekkie obramowanie */
}

.sidebar-navigation::-webkit-scrollbar-track {
  background-color: #f3f4f6; /* Jasnoszare tło */
  border-radius: 8px; /* Zaokrąglenie tła */
}

/* Pasek przewijania w trybie ciemnym */
.dark .sidebar-navigation::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.dark .sidebar-navigation::-webkit-scrollbar-thumb {
  background-color: #2c7a7a; /* Teal-700 */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #1a5d5d; /* Obramowanie ciemniejszego teal */
}

.dark .sidebar-navigation::-webkit-scrollbar-track {
  background-color: #333333; /* Ciemne tło dla śledzenia */
  border-radius: 8px; /* Zaokrąglenie tła */
}
</style>
